import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useState } from 'react';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import { ActiveItemsVirtualLazyLoadList } from '~components/Lists/ActiveItemsVirtualLazyLoadList';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import DebouncedSearchBox from '~components/Search/DebouncedSearchBox';
import { FetchReason } from '~constants/enums';
import { Account } from '~hooks/useAccount/models';
import { Driver } from '~hooks/useDrivers/models';

import { OwnerContext } from '../context/OwnerContext';
import CreateAutomaticSettlementAddonButton from './CreateAutomaticAddonButton';
import ScheduleOwnerQueryContext, {
  getOwnerFetchFailureMessage,
  ScheduleOwnerQueryContextProvider,
} from './ScheduleOwnerQueryContext';

interface ScheduleOwnerListContainerProps {
  companyId: string;
  ownerType: RateOwnerType;
}

export default function ScheduleOwnerListContainer({
  companyId,
  ownerType,
}: ScheduleOwnerListContainerProps) {
  const [search, setSearch] = useState({ companyId, term: '', seed: 0 });

  // Clearing during render rather than in an effect keeps the reset in the same
  // render as the company change: the search box remounts on `companyId` and an
  // effect lands a render too late to stop it re-adopting the outgoing
  // company's term.
  if (search.companyId !== companyId) {
    setSearch({ companyId, term: '', seed: 0 });
  }

  const handleSearchChange = useCallback((term: string) => {
    setSearch((state) => ({ ...state, term }));
  }, []);

  // The box owns its input value and only reads `initialValue` on mount, so a
  // term set out here has to remount it.
  const handleScheduleCreated = useCallback((ownerName: string) => {
    setSearch((state) => ({ ...state, term: ownerName, seed: state.seed + 1 }));
  }, []);

  return (
    <ScheduleOwnerQueryContextProvider
      companyId={companyId}
      ownerType={ownerType}
      searchQuery={search.term}
    >
      <CreateAutomaticSettlementAddonButton onScheduleCreated={handleScheduleCreated} />
      <Divider />
      <Box sx={{ px: 1.5, py: 1 }}>
        <DebouncedSearchBox
          key={`${companyId}:${search.seed}`}
          initialValue={search.term}
          onChange={handleSearchChange}
          placeholder={t('form_fields.search')}
        />
      </Box>
      <Divider />
      <ScheduleOwnerList />
    </ScheduleOwnerQueryContextProvider>
  );
}

const ScheduleOwnerList = () => {
  const scheduleOwnerQuery = useContext(ScheduleOwnerQueryContext);
  const { selectedOwnerId, setSelectedOwner, selectedOwnerType } =
    useContext(OwnerContext);

  const isInitialFetch = scheduleOwnerQuery.fetchReason === FetchReason.INITIAL_FETCH;
  const hasOwners = scheduleOwnerQuery.data.length > 0;
  const isDriverOwner = selectedOwnerType === RateOwnerType.DRIVER;

  const emptyStateMessage = scheduleOwnerQuery.isError
    ? getOwnerFetchFailureMessage(selectedOwnerType)
    : t('common.no_results');

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <LoadingSpinner isVisible={isInitialFetch} sx={{ height: '100%' }} />
      {!isInitialFetch &&
        (hasOwners ? (
          <ActiveItemsVirtualLazyLoadList
            activeItemId={selectedOwnerId}
            itemHeight={48}
            items={scheduleOwnerQuery.data}
            hasNextPage={scheduleOwnerQuery.hasMore}
            loadNextPage={scheduleOwnerQuery.fetchMore}
            onClickCallBack={(data: Account | Driver) =>
              setSelectedOwner(
                data.id,
                isDriverOwner ? (data as Driver).fullName : (data as Account).name,
              )
            }
            titleProperty={isDriverOwner ? 'fullName' : 'name'}
            excludeHeightOffset
          />
        ) : (
          <Box sx={{ px: 1.5, py: 2 }}>
            <EmptyStateMessage message={emptyStateMessage} />
          </Box>
        ))}
    </Box>
  );
};
